import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
import { machineKeyResponse } from "~/backend/validate.js";
import { query, paginatedResponse } from "~/lib/api.js";
import { SearchParams } from "~/lib/search.js";
import html from "~/util/html.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Time, { relative } from "~/component/Time.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import { ComboboxInput } from "~/admin/component/Combobox.js";
import FreshKeyCallout from "~/admin/component/FreshKeyCallout.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";

/** @import { orgResponse } from "~/backend/validate.js"; */
import adminStyle from "~/admin/lib/style.js";
import { addKey } from "~/admin/res/key/actions/addKey.js";
import { deleteKeys } from "~/admin/res/key/actions/deleteKeys.js";
import MemberChip from "~/admin/res/member/MemberChip.js";
import util from "~/style/util.js";

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 */
export default class KeysRoute extends Component {
  render() {
    return [__h(Crumb, {}, "Keys"), __h(Keys, {
      org: this.props.org
    })];
  }
}

/**
 * @extends {Component<KeysProps>}
 *
 * @typedef {object} KeysProps
 * @property {orgResponse} org
 */
class Keys extends Component {
  checked = signal(/** @type {Map<string, machineKeyResponse>} */new Map());
  paginate = new Pagination.State();
  sort = new Table.Sort({
    keys: ["name", "last_used_at", "created_at", "creator"],
    by: "created_at",
    order: "desc"
  });
  freshKey = signal(/** @type {FreshKey | null} */null);
  render() {
    return __h("div", {
      "class": adminStyle.main
    }, __h(FreshKeyCallout, {
      freshKey: this.freshKey.value
    }), __h(ComboboxInput, {
      query: SearchParams.q,
      debounce: 200
    }), __h("div", {
      "class": adminStyle.body
    }, __h(KeysActions, {
      orgId: this.props.org.id,
      checked: this.checked,
      freshKey: this.freshKey
    }), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, __h(KeysBody, {
      orgId: this.props.org.id,
      checked: this.checked,
      paginate: this.paginate,
      sort: this.sort
    }))));
  }
}

/**
 * @extends {Component<ActionsProps>}
 *
 * @typedef {object} ActionsProps
 * @property {string} orgId
 * @property {typeof Keys.prototype.checked} checked
 * @property {typeof Keys.prototype.freshKey} freshKey
 */
class KeysActions extends Component {
  render() {
    const checked = this.props.checked;
    const bulk = checked.value.size > 0;
    return __h("menu", {
      "class": adminStyle.menu
    }, __h(Button, {
      type: "button",
      size: "small",
      onClick: async () => {
        const res = await addKey({
          orgId: this.props.orgId
        });
        if (res) this.props.freshKey.value = res;
      }
    }, __h(Icon, {
      name: "plus",
      size: 12
    }), __h("span", {}, "Generate new key")), __h(Button, {
      disabled: !bulk,
      size: "small",
      kind: "negative",
      onClick: async () => {
        const res = await deleteKeys({
          orgId: this.props.orgId
        }, ...checked.value.keys());
        if (res) checked.value = new Map();
      }
    }, __h(Icon, {
      name: "trash",
      size: 12
    }), "Delete"));
  }
}

/**
 * @extends {Component<BodyProps>}
 *
 * @typedef {object} BodyProps
 * @property {string} orgId
 * @property {typeof Keys.prototype.checked} checked
 * @property {typeof Keys.prototype.paginate} paginate
 * @property {typeof Keys.prototype.sort} sort
 */
class KeysBody extends Component {
  machineKeys = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/machine-keys`, {
      ...this.props.paginate.query(),
      ...this.props.sort.query(),
      q: SearchParams.q.value
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(machineKeyResponse)
  });
  render() {
    const keys = this.machineKeys;
    const rows = keys.data?.data.map(key => ({
      key: key.id,
      label: key.name,
      value: key,
      columns: {
        name: __h("div", {
          "class": adminStyle.withDesc
        }, __h("span", {
          "class": util.truncate
        }, key.name), __h("span", {
          "class": util.truncate + " " + adminStyle.desc
        }, key.description)),
        lastUsedAt: key.last_used_at && __h(Time, {
          fmt: relative,
          datetime: new Date(key.last_used_at)
        }),
        createdAt: __h(Time, {
          fmt: relative,
          datetime: new Date(key.created_at)
        }),
        createdBy: key.user && __h(MemberChip, {
          member: {
            ...key.user,
            org_id: this.props.orgId
          }
        })
      }
    }));
    return h(Data, {
      loading: keys.loading && !keys.data,
      error: keys.error,
      children: h(/** @type {typeof Table<machineKeyResponse>} */Table, {
        label: "key",
        checked: this.props.checked.value,
        onCheck: check => this.props.checked.value = check,
        empty: SearchParams.q.value ? `No machine keys match "${SearchParams.q.value}".` : [__h("strong", {}, "There are no machine keys."), __h("div", {}, "Keys are used to register new managed machines with your organization.")],
        headers: [{
          id: "name",
          label: "Name",
          sort: "name"
        }, {
          id: "lastUsedAt",
          label: "Last used at",
          empty: "Never",
          sort: "last_used_at"
        }, {
          id: "createdAt",
          label: "Created at",
          sort: "created_at"
        }, {
          id: "createdBy",
          label: "Created by",
          empty: "Unknown user",
          sort: "creator"
        }],
        rows: rows ?? [],
        paginate: this.props.paginate.props(keys),
        sort: this.props.sort.props(),
        onTripleDots: key => [{
          icon: "trash",
          text: "Delete key",
          kind: "negative",
          onSelect: async () => {
            const res = await deleteKeys({
              orgId: this.props.orgId
            }, key.id);
            if (res) {
              const checked = new Map(this.props.checked.value);
              checked.delete(key.id);
              this.props.checked.value = checked;
            }
          }
        }]
      })
    });
  }
}